/**
 * Simple Mock Adapter - Clean Session API
 *
 * Complete session management system:
 * - Owns ALL session data and persistence
 * - Self-contained localStorage handling
 * - Clean API: all methods take sessionId when needed
 * - No external sync required
 *
 * TD-1845: Uses shared storage utilities for user-scoped keys and logout cleanup
 * to prevent cross-user data exposure (same security as AgentCoreAdapter)
 */

import pubSub from '../../../utils/pubsub';
import {
  getCurrentUserId,
  loadStoredSessions,
  loadStoredMessages,
  saveStoredSessions,
  saveStoredMessages,
  createLogoutHandler
} from './adapterStorageUtils';

const ADAPTER_NAME = 'SIMPLE MOCK';

class SimpleMockAdapter {
  constructor(store = null) {
    console.log('🧪 SIMPLE MOCK: Initializing with complete session persistence');

    // TD-1845: Store reference for user ID access
    this.store = store;

    // TD-1845: Cache user ID on construction for logout cleanup
    // Using object ref to allow mutation in logout handler
    this._cachedUserIdRef = { value: getCurrentUserId(store, ADAPTER_NAME) };

    // Load all persisted data on construction using shared utilities
    this.sessions = loadStoredSessions(store, ADAPTER_NAME);
    this.messages = loadStoredMessages(store, ADAPTER_NAME);
    this.messageCallbacks = [];
    this.stateCallbacks = [];
    this.sessionsRefreshedCallbacks = [];

    // TD-1845: Subscribe to logout event to clear user data
    const logoutCleanup = createLogoutHandler(
      store,
      this.sessions,
      this.messages,
      this._cachedUserIdRef,
      ADAPTER_NAME
    );

    this._logoutHandler = (payload) => {
      if (payload?.event === 'logout') {
        logoutCleanup();
      }
    };
    pubSub.subscribe(this._logoutHandler);

    console.log(
      '📂 SIMPLE MOCK: Loaded',
      this.sessions.size,
      'sessions and',
      this.messages.size,
      'message sets'
    );
  }

  // ============================================================================
  // SECURITY: User Identity Access (TD-1845)
  // ============================================================================

  /**
   * Get current user ID from auth store.
   * Delegated to shared utility for consistency across adapters.
   * @returns {string|null} Current user's ID or null if not authenticated
   */
  _getCurrentUserId() {
    return getCurrentUserId(this.store, ADAPTER_NAME);
  }

  // ============================================================================
  // PERSISTENCE LAYER (Private) - Delegates to shared utilities
  // ============================================================================

  loadSessions() {
    return loadStoredSessions(this.store, ADAPTER_NAME);
  }

  loadMessages() {
    return loadStoredMessages(this.store, ADAPTER_NAME);
  }

  saveSessions() {
    saveStoredSessions(this.store, this.sessions, ADAPTER_NAME);
  }

  saveMessages() {
    saveStoredMessages(this.store, this.messages, ADAPTER_NAME);
  }

  // ============================================================================
  // SESSION API (Public)
  // ============================================================================

  async createSession() {
    const sessionId = `simple_session_${Date.now()}_${Math.random().toString(36).substr(2, 8)}`;

    const session = {
      id: sessionId,
      createdAt: new Date(),
      title: 'New Chat' // Will be updated with first message
    };

    // Store session
    this.sessions.set(sessionId, session);
    this.messages.set(sessionId, []);

    // Persist immediately
    this.saveSessions();
    this.saveMessages();

    console.log('📝 SIMPLE MOCK: Session created:', sessionId);

    return {
      sessionId,
      runtimeSessionId: `runtime_${sessionId}`,
      mcpSessionId: `mcp_${sessionId}`
    };
  }

  async connect(sessionId) {
    console.log('🔌 SIMPLE MOCK: Connected to session:', sessionId);

    // Verify session exists
    if (!this.sessions.has(sessionId)) {
      throw new Error(`Session not found: ${sessionId}`);
    }
  }

  async sendMessage(sessionId, content, options = {}) {
    console.log('💬 SIMPLE MOCK: Received message for session:', sessionId);

    const { sourceMessageId = null } = options;

    // TD-1845 Fix #7: Verify user is authenticated before modifying messages
    // This prevents edge case where User A's pending message send completes after
    // User B logs in (without page reload), potentially saving to wrong user's storage
    const userId = this._getCurrentUserId();
    if (!userId) {
      throw new Error('Cannot send message: No authenticated user');
    }

    // Verify session exists
    if (!this.sessions.has(sessionId)) {
      throw new Error(`Session not found: ${sessionId}`);
    }

    const sessionMessages = this.messages.get(sessionId) || [];

    // 1. Add user message
    const userMessage = {
      id: `msg_${Date.now()}_user`,
      type: 'user',
      content,
      timestamp: new Date(),
      sourceMessageId
    };

    sessionMessages.push(userMessage);
    this.messages.set(sessionId, sessionMessages);
    this.saveMessages();

    // Update session title with first message
    const session = this.sessions.get(sessionId);
    if (session.title === 'New Chat') {
      session.title = content.length > 50 ? content.substring(0, 50) + '...' : content;
      this.sessions.set(sessionId, session);
      this.saveSessions();
    }

    // Notify user message
    this._notifyMessage(sessionId, userMessage);

    // 🔥 FIX: Set agent to thinking state
    console.log('🤔 SIMPLE MOCK: Agent is now thinking...');
    this._notifyStateChange(sessionId, 'thinking');

    // 2. Generate AI response after delay
    setTimeout(() => {
      // 🔥 FIX: Set agent to responding state
      console.log('🤖 SIMPLE MOCK: Agent is now responding...');
      this._notifyStateChange(sessionId, 'responding');

      // TD-2293: scripted quote turn — a message asking to reveal produces the
      // confirm card (message.enrichment.revealPending), mirroring the shipped
      // [REVEAL_PENDING:<token>:<cost>:<remaining>] contract without a backend.
      const isRevealAsk = /\breveal\b/i.test(content);
      const aiMessage = {
        id: `msg_${Date.now()}_ai`,
        type: 'ai',
        content: isRevealAsk
          ? "That's 3 contacts — Work Email for each costs 3 credits total. You have 99 credits remaining. Confirm to proceed."
          : `I received your message: "${content}". This is a simple mock response.`,
        enrichment: isRevealAsk
          ? { revealPending: { token: 'bW9jaw.cGF5bG9hZA', cost: 3, remaining: 99 } }
          : null,
        timestamp: new Date()
      };

      // Get the current messages array
      const currentMessages = this.messages.get(sessionId);
      if (!currentMessages) {
        console.error('❌ SIMPLE MOCK: Session messages not found:', sessionId);
        this._notifyStateChange(sessionId, 'idle');
        return;
      }

      console.log(
        '🤖 SIMPLE MOCK: Adding AI response with',
        currentMessages.length,
        'existing messages'
      );
      currentMessages.push(aiMessage);
      this.messages.set(sessionId, currentMessages);
      this.saveMessages();

      console.log('🤖 SIMPLE MOCK: Session now has', currentMessages.length, 'total messages');
      this._notifyMessage(sessionId, aiMessage);

      // 🔥 FIX: Set agent back to idle state
      console.log('✅ SIMPLE MOCK: Agent is now idle');
      this._notifyStateChange(sessionId, 'idle');
    }, 500);
  }

  /**
   * TD-2293: parity with AgentCoreAdapter.touchSession — the chat page touches
   * the session on open (useAgentChatSession) and the provider calls this
   * unguarded, so the mock must implement it (local lastActivity bump only).
   */
  touchSession(sessionId) {
    const session = this.sessions.get(sessionId);
    if (!session) {
      return null;
    }
    session.lastActivity = new Date();
    this.sessions.set(sessionId, session);
    this.saveSessions();
    return session;
  }

  /**
   * TD-2293: mock parity for the hidden reveal control replies. Mirrors the
   * deterministic td-agent harness strings so the confirm-card flow can be
   * exercised end-to-end without a backend: the hidden user message never
   * renders; the scripted AI response arrives as a visible turn.
   */
  async sendControlReply(sessionId, content) {
    console.log('👻 SIMPLE MOCK: Control reply for session:', sessionId, content);

    if (!this.sessions.has(sessionId)) {
      throw new Error(`Session not found: ${sessionId}`);
    }

    const sessionMessages = this.messages.get(sessionId) || [];
    sessionMessages.push({
      id: `msg_${Date.now()}_user_control`,
      type: 'user',
      content,
      timestamp: new Date(),
      hidden: true
    });
    this.messages.set(sessionId, sessionMessages);
    this.saveMessages();

    let responseContent;
    let enrichment = null;
    if (content.startsWith('CONFIRM_REVEAL:')) {
      responseContent =
        'Here are the revealed contact details:\n\n' +
        '| Contact | Work Email | Mobile Phone |\n|---|---|---|\n' +
        '| Tobias Lang (mock) | tobias.lang@example.com | +49 170 0000000 |\n\n' +
        'Entries with no data found cost nothing.';
    } else if (content.startsWith('CANCEL_REVEAL:')) {
      // Exact deterministic ack from td-agent events/reveal_confirm.py.
      responseContent = "Understood — I've cancelled the reveal. No credits were spent.";
    } else if (content.startsWith('CHECK_REVEAL_RESULTS:')) {
      responseContent =
        'The bulk reveal finished: 2 revealed, 1 not found (not charged).';
    } else {
      responseContent = `Mock control acknowledgement for: ${content}`;
    }

    setTimeout(() => {
      const currentMessages = this.messages.get(sessionId);
      if (!currentMessages) return;
      const aiMessage = {
        id: `msg_${Date.now()}_ai_control`,
        type: 'ai',
        content: responseContent,
        enrichment,
        timestamp: new Date()
      };
      currentMessages.push(aiMessage);
      this.messages.set(sessionId, currentMessages);
      this.saveMessages();
      this._notifyMessage(sessionId, aiMessage);
    }, 400);
  }

  // ============================================================================
  // DATA ACCESS API (Public)
  // ============================================================================

  getSession(sessionId) {
    return this.sessions.get(sessionId) || null;
  }

  getMessages(sessionId) {
    const messages = this.messages.get(sessionId) || [];
    console.log(
      '📋 SIMPLE MOCK: getMessages for session',
      sessionId,
      'returning',
      messages.length,
      'messages'
    );
    return messages;
  }

  async getAllSessions() {
    // Made async for API consistency with AgentCoreAdapter
    // Mock adapter returns local data immediately but wrapped in Promise
    const sessions = Array.from(this.sessions.values());

    // TD-2571: honour the same settle contract as the real adapter — exactly one event per call.
    // There is no backend here, so the local map IS the settled answer. Without this the mock
    // (the default for local dev and tests) would never settle and a missing thread would hang,
    // which is the very bug this fixes.
    this._notifySessionsRefreshed(sessions, true);

    return sessions;
  }

  async getRecentSessions(limit = 10) {
    // Made async for API consistency with AgentCoreAdapter
    const allSessions = await this.getAllSessions();
    return [...allSessions].sort((a, b) => b.createdAt - a.createdAt).slice(0, limit);
  }

  // ============================================================================
  // EVENT SYSTEM (Public)
  // ============================================================================

  onMessage(callback) {
    this.messageCallbacks.push(callback);
    return () => {
      const idx = this.messageCallbacks.indexOf(callback);
      if (idx !== -1) this.messageCallbacks.splice(idx, 1);
    };
  }

  onStateChange(callback) {
    this.stateCallbacks.push(callback);
    return () => {
      const idx = this.stateCallbacks.indexOf(callback);
      if (idx !== -1) this.stateCallbacks.splice(idx, 1);
    };
  }

  onSearchProgress() {
    return () => {};
  }

  onSessionsRefreshed(callback) {
    this.sessionsRefreshedCallbacks.push(callback);
    return () => {
      const idx = this.sessionsRefreshedCallbacks.indexOf(callback);
      if (idx !== -1) this.sessionsRefreshedCallbacks.splice(idx, 1);
    };
  }

  _notifySessionsRefreshed(sessions, ok) {
    this.sessionsRefreshedCallbacks.forEach((callback) => {
      try {
        callback({ sessions, ok });
      } catch (error) {
        console.error('❌ MOCK: sessions refreshed callback threw:', error);
      }
    });
  }

  _notifyMessage(sessionId, message) {
    const event = {
      type: 'message',
      sessionId,
      messageId: message.id,
      messageType: message.type,
      content: message.content,
      message
    };

    console.log('📨 SIMPLE MOCK: Notifying message:', event);

    this.messageCallbacks.forEach((callback) => {
      try {
        callback(event);
      } catch (error) {
        console.error('Error in message callback:', error);
      }
    });
  }

  _notifyStateChange(sessionId, newState) {
    console.log('🔄 SIMPLE MOCK: Notifying state change:', sessionId, newState);

    // TD-1904: Pass structured event with sessionId for per-session tracking
    this.stateCallbacks.forEach((callback) => {
      try {
        callback({ sessionId, state: newState });
      } catch (error) {
        console.error('Error in state callback:', error);
      }
    });
  }

  // ============================================================================
  // COMPATIBILITY (Public)
  // ============================================================================

  getConnectionStatus() {
    return 'connected';
  }

  // No longer needed - adapter is self-contained
  registerPersistedSession() {
    console.warn(
      '⚠️ SIMPLE MOCK: registerPersistedSession not needed - adapter handles own persistence'
    );
  }

  // ============================================================================
  // CLEANUP (Public)
  // ============================================================================

  destroy() {
    console.log('🧹 SIMPLE MOCK: Cleaning up adapter');

    // TD-1845: Clean up pubSub subscription to prevent memory leaks
    if (this._logoutHandler) {
      pubSub.unsubscribe(this._logoutHandler);
      this._logoutHandler = null;
    }

    this.messageCallbacks = [];
    this.stateCallbacks = [];
    this.sessionsRefreshedCallbacks = [];
  }
}

export default SimpleMockAdapter;
